/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import ChannelContext from '../context/ChannelContext';
import Discovery from '../discovery/Discovery';
import Disposable from '../../lang/Disposable';
import DisposableList from '../../lang/DisposableList';
import LoggerFactory from '../../logger/LoggerFactory';
import ChannelState from '../channels/ChannelState';
import {ChunkedStreaming} from './ChunkedStreaming';
import {ChunkedStreamingOptions, ChunkedStreamingStatistics} from './StreamTypes';
import IDisposable from '../../lang/IDisposable';
import {IStreamSubscriber} from './IStreamSubscriber';
import {ILogger} from '../../logger/LoggerInterface';
import {NetworkStates} from './NetworkStates';
import ChunkedStreamPlaybackMode from './ChunkedStreamPlaybackMode';
import VideoFrameDetector from './VideoFrameDetector';

export default class ChunkedStreamSubscriber implements IStreamSubscriber {
  private readonly _logger: ILogger = LoggerFactory.getLogger('ChunkedStreamSubscriber');
  private readonly _channelContext: ChannelContext;
  private readonly _chunkedStreamPlaybackMode: ChunkedStreamPlaybackMode;
  private readonly _disposables = new DisposableList();
  private readonly _monitorDisposables = new DisposableList();
  private readonly _handleStreamFailure: () => Promise<void>;
  private _streamStatistics: ChunkedStreamingStatistics | null = null;
  private _monitorFailureCount = 0;

  constructor(channelContext: ChannelContext, chunkedStreamPlaybackMode: ChunkedStreamPlaybackMode, handleStreamFailure: () => Promise<void>) {
    this._channelContext = channelContext;
    this._chunkedStreamPlaybackMode = chunkedStreamPlaybackMode;
    this._handleStreamFailure = handleStreamFailure;
    this._channelContext.disposables.add(this);
  }

  start(uri: URL, token: string): Promise<void | IDisposable> {
    return Discovery.discoverClosestEndPointWithCaching(uri)
      .then(endPoint => {
        this._channelContext.online.value = true;
        this._channelContext.endPoint.value = endPoint;
        this._logger.info('Connecting to [%s]', endPoint.toString());

        return endPoint.subscribe(token, null, this._channelContext.failureCount.value);
      })
      .then(response => {
        const {status} = response;
        const endPoint = this._channelContext.endPoint.value;

        if (!endPoint) {
          this._logger.error('Subscribe failed: end point service is missing');
          this._channelContext.loading.value = false;
          this._channelContext.playing.value = false;
          this._channelContext.state.value = ChannelState.Error;

          return;
        }

        this._channelContext.exists.value = this._channelContext.mapSubscribeStatusToExists(status);
        this._channelContext.state.value = this._channelContext.mapSubscribeStatusToChannelStatus(status);

        this._channelContext.applyStatus(status);

        if (status !== 'ok') {
          this._channelContext.stream.value = null;
          this._channelContext.lag.value = undefined ;

          this._channelContext.applySessionAndStreamPropertiesToVideoElement();

          this._logger.debug(
            '[%s] Subscribe failed with status [%s]',
            this._channelContext.streamId,
            status,
          );

          return;
        }

        const {stream, createOfferDescriptionResponse, lag} = response;

        this._channelContext.stream.value = stream;
        this._channelContext.lag.value = lag;

        this._channelContext.applySessionAndStreamPropertiesToVideoElement();

        this._logger.debug(
          '[%s] Subscribe completed [%s] [%j]',
          this._channelContext.streamId,
          status,
          createOfferDescriptionResponse,
        );

        const offerSdp = createOfferDescriptionResponse?.sessionDescription?.sdp;
        const options: ChunkedStreamingOptions = {
          originStartTime: Date.now() - lag + (endPoint.roundTripTime) / 2,
          lag: lag
        };

        if (!this._channelContext.videoElement.value) {
          this._logger.error('Subscribe failed: video element is missing');
          this._channelContext.loading.value = false;
          this._channelContext.playing.value = false;
          this._channelContext.state.value = ChannelState.Stopped;

          return;
        }

        if (!offerSdp) {
          this._logger.error('Subscribe failed: offer SDP is missing');
          this._channelContext.loading.value = false;
          this._channelContext.playing.value = false;
          this._channelContext.state.value = ChannelState.Error;

          return;
        }

        return ChunkedStreaming.start(this._channelContext.videoElement.value, this._channelContext.streamId, offerSdp, options, () => this.retryOnStreamFailure())
          .then(player => {
            this.monitor(player);

            this._disposables.add(player);

            return player;
          })
          .catch(e => {
            this._logger.error('ChunkedStreaming failed', e);
            this._channelContext.loading.value = false;
            this._channelContext.playing.value = false;
            this._channelContext.state.value = ChannelState.Stopped;
          });
      });
  }

  private retryOnStreamFailure(): void {
    this._monitorFailureCount = 0;
    this._channelContext.state.value = ChannelState.ConnectionError;

    this._channelContext.playing.value = false;
    this._channelContext.loading.value = true;
    this._disposables.dispose();
    this._monitorDisposables.dispose();

    const ignored = this._handleStreamFailure()
      .catch(e => {
        this._logger.error('Failed handling stream failure', e);
      });
  }

  private monitor(player: IDisposable): void {
    this._monitorDisposables.dispose();

    const timeout = GlobalScope.value.setTimeout(() => {
      const newStreamStatistics = ChunkedStreaming.getStats();

      if (this._chunkedStreamPlaybackMode === ChunkedStreamPlaybackMode.Live
        && newStreamStatistics?.networkState === NetworkStates.NetworkNoSource
        && this._channelContext.videoElement.value?.currentTime === this._channelContext.videoElement.value?.duration
      ) {
        this.retryOnStreamFailure();

        return;
      }

      if (this._streamStatistics &&
        !this._channelContext.videoElement.value?.paused &&
          (newStreamStatistics?.currentTime && this._streamStatistics.currentTime >= newStreamStatistics.currentTime) &&
          (!newStreamStatistics?.dataBuffered || this._streamStatistics.dataBuffered >= newStreamStatistics.dataBuffered)
      ) {
        this._monitorFailureCount++;

        if (this._monitorFailureCount > 4) {
          this.retryOnStreamFailure();

          return;
        }
      } else {
        this._monitorFailureCount = 0;
      }

      if (this._channelContext.videoElement.value?.paused && this._channelContext.state.value === ChannelState.Playing) {
        this._channelContext.state.value = ChannelState.Paused;
      }

      if (!this._channelContext.videoElement.value?.paused && this._channelContext.state.value !== ChannelState.Playing) {
        this._channelContext.state.value = ChannelState.Playing;
      }

      this._streamStatistics = newStreamStatistics;

      this.monitor(player);
    }, 1000);

    this._monitorDisposables.add(new Disposable(() => {
      GlobalScope.value.clearTimeout(timeout);
    }));
  }

  isStreamHealthy(): Promise<boolean> {
    const videoElement = this._channelContext.videoElement.value;

    if (!videoElement) {
      return Promise.resolve(false);
    }

    if (videoElement.paused) {
      return Promise.resolve(false);
    }

    return VideoFrameDetector.isRenderingFrames(videoElement);
  }

  dispose(): void {
    this._disposables.dispose();
    this._monitorDisposables.dispose();
  }
}